import type {
  Connection,
  EnterpriseConnection,
} from '@auth0/auth0-acul-react/types';
import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import { useTranslation } from 'react-i18next';

import Flex from '@/components/Layout/Flex/Flex';
import Text from '@/components/Typography/Text/Text';
import { trackClick } from '@/lib/tagging';
import { PASSKEY_INFO_SEEN_KEY } from '@/screens/login-id/components/PasskeyInfoForm';
import { useLoginIdManager } from '@/screens/login-id/hooks/useLoginIdManager';

interface LandingFormProps {
  onShowEmail: () => void;
  onShowPasskeyInfo: () => void;
  onShowPasskeyLoginInfo: () => void;
}

const isEnterpriseConnection = (
  c: Connection | EnterpriseConnection,
): c is EnterpriseConnection => {
  return 'options' in c;
};

export function LandingForm({
  onShowEmail,
  onShowPasskeyInfo,
  onShowPasskeyLoginInfo,
}: Readonly<LandingFormProps>) {
  const { t } = useTranslation('loginId');
  const {
    handlePasskeyLogin,
    handleSignup,
    handleFederatedLogin,
    alternateConnections,
    isPasskeyEnabled,
    isSignupEnabled,
  } = useLoginIdManager();

  const handlePasskeyClick = async () => {
    trackClick('login/passkey');
    if (localStorage.getItem(PASSKEY_INFO_SEEN_KEY) === 'true') {
      await handlePasskeyLogin();
    } else {
      onShowPasskeyLoginInfo();
    }
  };

  const handleAlternateConnectionClick = async (connectionName: string) => {
    trackClick(`login/${connectionName}`);
    await handleFederatedLogin({ connection: connectionName });
  };

  const handleEmailClick = () => {
    trackClick('login/credentials');
    onShowEmail();
  };

  const handleSignupClick = async () => {
    trackClick('login/selfactivation');
    await handleSignup();
  };

  return (
    <Flex direction="column" gapY={32}>
      <Flex direction="column" gapY={16}>
        {isPasskeyEnabled && (
          <CtaContainer stack>
            <Cta type="primary" onClick={handlePasskeyClick}>
              {t('landingForm.passkeyButtonText')}
            </Cta>
          </CtaContainer>
        )}
        {alternateConnections?.map((connection) => {
          return (
            <CtaContainer
              key={`${connection.strategy}-${connection.name}`}
              stack>
              <Cta
                type="secondary"
                onClick={() => handleAlternateConnectionClick(connection.name)}>
                {isEnterpriseConnection(connection) &&
                connection.options.iconUrl ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}>
                    <img
                      src={connection.options.iconUrl}
                      alt={`${connection.name} icon`}
                      style={{ width: 16, height: 16, objectFit: 'contain' }}
                    />
                    {t(`landingForm.connections.${connection.name}`)}
                  </span>
                ) : (
                  t(`landingForm.connections.${connection.name}`)
                )}
              </Cta>
            </CtaContainer>
          );
        })}
        <CtaContainer stack>
          <Cta type="secondary" onClick={handleEmailClick}>
            {t('landingForm.emailButtonText')}
          </Cta>
        </CtaContainer>
      </Flex>
      <Flex direction="column" gapY={16} align="center">
        {isPasskeyEnabled && (
          <Flex justify="center" align="center" className="flex-wrap" gapX={8}>
            <Text>{t('landingForm.noPasskeyText')}</Text>
            <Cta type="ghost-inline" onClick={onShowPasskeyInfo}>
              {t('landingForm.infoButtonText')}
            </Cta>
          </Flex>
        )}

        {isSignupEnabled && (
          <Flex justify="center" align="center" className="flex-wrap" gapX={8}>
            <Text>{t('landingForm.noAccountText')}</Text>
            <Cta type="ghost-inline" onClick={handleSignupClick}>
              {t('landingForm.signupButtonText')}
            </Cta>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
